/**
 * Dashboard Component
 *
 * Main landing page for authenticated doctors. Displays key stats
 * (active cases, pending requests, revenue), recent activity feed,
 * and navigation shortcuts. Handles E2EE passphrase entry for
 * decrypting sensitive case data.
 *
 * @module components/dashboard
 */

import {
  AlertCircle,
  Bell,
  Check,
  CheckCircle,
  Clock,
  Copy,
  DollarSign,
  FileText,
  FolderOpen,
  Gift,
  Loader2,
  Lock,
  Mail,
  Minus,
  RefreshCw,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';
import {
  apiClient,
  type DashboardNotification,
  type DashboardStats,
  type PaymentData,
  type PreScreeningRequest,
  type RecentActivity,
  type ReferralStats,
} from '../api';
import { useAuth } from '../contexts/auth-context';
import { useEncryption } from '../contexts/EncryptionContext';
import { useDoctorSubscription } from '../hooks/useDoctorSubscription';
import { useProductTour } from '../hooks/useProductTour';
import { doctorDashboardSteps } from '../tours';
import { trackInviteEvent } from '../utils/inviteEmailUtils';
import { safeSetItem } from '../utils/safe-storage';
import { useAutoEncryptionMigration } from './BatchEncryptionMigration';
import { DoctorSubscriptionCard } from './DoctorSubscriptionCard';
import { HeroBanner } from './hero-banner';
import { HowItWorksModal } from './how-it-works-modal';
import { InviteClientsModal } from './invite-clients-modal';
import { LearnMoreLink } from './LearnMoreLink';
import { PassphraseEntry } from './passphrase-entry';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface DashboardProps {
  onViewCase: (caseId: string) => void;
  onNavigateToRequests: () => void;
  onNavigateToSettings: () => void;
  onNavigateToRequest?: (requestId: string) => void;
  onNavigateToInvoice?: (invoiceId: string) => void;
  onNavigateToCase?: (caseId: string) => void;
}

export function Dashboard({
  onViewCase,
  onNavigateToRequests,
  onNavigateToSettings,
  onNavigateToRequest,
  onNavigateToInvoice,
  onNavigateToCase,
}: DashboardProps) {
  const { user } = useAuth();
  const {
    decryptField,
    status: encryptionStatus,
    unlockEncryption,
    lockEncryption,
    isUnlocked,
    isShowingModal: isEncryptionModalOpen,
  } = useEncryption();

  // Silently migrate legacy encryption → envelope encryption in the background
  useAutoEncryptionMigration();

  // Doctor subscription state powers the top-of-dashboard status card.
  // Fetch is enabled for DOCTOR / TEAM_MEMBER / ADMIN — admins are also
  // practicing doctors on this platform and see the same doctor-facing UI
  // (the gate itself bypasses them via reason:'admin', so no paywall).
  const isDoctorContext =
    user?.role === 'DOCTOR' || user?.role === 'TEAM_MEMBER' || user?.role === 'ADMIN';
  const { state: doctorSubscriptionState, refresh: refreshDoctorSubscription } =
    useDoctorSubscription(isDoctorContext);
  // First-login modal sequencing: when the paywall overlay is up (DoctorAccessGate
  // renders it), the dashboard shouldn't also fire the onboarding modal or start
  // the product tour behind it. Wait for the subscription check to resolve for
  // doctor contexts so we don't flash the onboarding modal before knowing.
  const isSubscriptionCheckPending = isDoctorContext && doctorSubscriptionState === null;
  const isPaywalled =
    isDoctorContext && doctorSubscriptionState !== null && !doctorSubscriptionState.allowed;

  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentData[]>([]);
  const [recentRequests, setRecentRequests] = useState<PreScreeningRequest[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [billingSetupComplete, setBillingSetupComplete] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [copiedReferral, setCopiedReferral] = useState(false);
  // Stays true until we know whether the onboarding modal will show.
  // Closes the async gap between isLoading=false and showOnboardingModal=true.
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);
  // Prevents duplicate onboarding checks when user object reference changes
  const onboardingCheckedRef = useRef(false);
  const welcomeToastIdRef = useRef<string | number | null>(null);

  useProductTour({
    tourId: 'doctor-dashboard',
    steps: doctorDashboardSteps,
    disabled:
      isLoading ||
      isCheckingOnboarding ||
      showOnboardingModal ||
      showUnlockModal ||
      isInviteModalOpen ||
      isEncryptionModalOpen ||
      // Don't run the tour behind the paywall or before we know whether one
      // needs to render — the user hasn't earned access yet, and the tour
      // pointing at UI they can't interact with is disorienting.
      isSubscriptionCheckPending ||
      isPaywalled,
  });

  // Load referral stats
  useEffect(() => {
    if (user) {
      apiClient
        .getReferralStats({ silent: true })
        .then(setReferralStats)
        .catch(() => {});
    }
  }, [user]);

  // Track last refresh time to implement throttling
  const lastRefreshRef = useRef<number>(0);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Store reference to loadDashboardData to avoid stale closures in throttledRefresh
  const loadDashboardDataRef = useRef<((silent?: boolean) => Promise<void>) | undefined>(undefined);

  // Minimum time between dashboard refreshes (5 seconds)
  const MIN_REFRESH_INTERVAL_MS = 5000;

  // Debounced/throttled refresh function to prevent connection pool exhaustion
  const throttledRefresh = useCallback((silent = true) => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshRef.current;

    // If we refreshed recently, schedule a delayed refresh instead of immediate
    if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL_MS) {
      // Clear any existing scheduled refresh
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // Schedule refresh for when cooldown period ends
      const delay = MIN_REFRESH_INTERVAL_MS - timeSinceLastRefresh;
      refreshTimeoutRef.current = setTimeout(() => {
        lastRefreshRef.current = Date.now();
        // Use ref to get latest loadDashboardData function
        loadDashboardDataRef.current?.(silent);
      }, delay);
      return;
    }

    // Enough time has passed, refresh immediately
    lastRefreshRef.current = now;
    // Use ref to get latest loadDashboardData function
    loadDashboardDataRef.current?.(silent);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dep list; loader fn identity changes every render (pre-existing pattern)
  useEffect(() => {
    loadDashboardData();
    lastRefreshRef.current = Date.now();
  }, [user]);

  // Reload data when encryption status changes (both locked and unlocked)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dep list; loader fn identity changes every render (pre-existing pattern)
  useEffect(() => {
    if (user) {
      throttledRefresh(true); // Throttled silent refresh
    }
  }, [isUnlocked, throttledRefresh]);

  // Auto-refresh when window regains focus (user comes back to tab)
  // Throttled to prevent connection pool exhaustion from rapid tab switching
  useEffect(() => {
    const handleFocus = () => {
      if (!isLoading && !isRefreshing) {
        throttledRefresh(true); // Throttled silent refresh
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      // Cleanup any pending throttled refresh on unmount
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [isLoading, isRefreshing, throttledRefresh]);

  // Check for first-time user and show onboarding modal.
  // Sequence: Paywall (if any) → Encryption Setup/Unlock → Onboarding → Product Tour.
  // Waits for the doctor-subscription check to resolve AND for any paywall to
  // clear before firing — otherwise a first-time doctor sees the onboarding
  // modal stacked on top of the paywall overlay + product tour.
  useEffect(() => {
    if (
      !isLoading &&
      user &&
      !isEncryptionModalOpen &&
      !isSubscriptionCheckPending &&
      !isPaywalled &&
      !onboardingCheckedRef.current
    ) {
      onboardingCheckedRef.current = true;
      const ONBOARDING_KEY = `expertmd_onboarding_shown_${user.clerkId}`;
      const hasSeenOnboarding = localStorage.getItem(ONBOARDING_KEY);

      if (!hasSeenOnboarding) {
        safeSetItem(ONBOARDING_KEY, 'true');
        const openGuide = async () => {
          try {
            const response = await apiClient.getStripeAccountStatus();
            setBillingSetupComplete(response.status?.detailsSubmitted || false);
          } catch (error) {
            console.error('[Dashboard] Failed to check billing status:', error);
            setBillingSetupComplete(false);
          }
          setShowOnboardingModal(true);
        };
        welcomeToastIdRef.current = toast('Welcome to ExpertMD!', {
          description:
            'New here? Open the quick-start guide, or take a page tour anytime from the Help menu.',
          duration: 15000,
          action: {
            label: 'View guide',
            onClick: () => {
              void openGuide();
            },
          },
        });
      }
      setIsCheckingOnboarding(false);
    }
  }, [isLoading, user, isEncryptionModalOpen, isSubscriptionCheckPending, isPaywalled]);

  // The welcome toast is rendered by the app-level Toaster and would outlive
  // this page, leaving its guide action a no-op — dismiss it on unmount.
  useEffect(
    () => () => {
      if (welcomeToastIdRef.current !== null) {
        toast.dismiss(welcomeToastIdRef.current);
      }
    },
    [],
  );

  const loadDashboardData = async (silent = false) => {
    if (!user) return;

    try {
      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      let userIdentifier = user.vanityUrl;

      // If vanityUrl is not in session, fetch it from settings API
      if (!userIdentifier) {
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3005/api/v1';
          const response = await fetch(`${apiUrl}/settings/vanity-url`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            userIdentifier = data.data?.vanityUrl;
          }
        } catch (err) {
          console.warn('[Dashboard] Failed to fetch vanityUrl from API:', err);
        }
      }

      // Final fallback to email-based slug
      if (!userIdentifier) {
        userIdentifier = user.email
          .split('@')[0]
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-');
      }

      // FIXED: Replace concurrent Promise.all with sequential calls to prevent connection pool exhaustion
      // Each API call may spawn multiple database connections (e.g., getDashboardStats = 8 queries)
      // Sequential approach limits concurrent connections to single endpoint's requirement

      // Circuit breaker: Stop loading remaining data if 503 errors indicate connection pool issues
      let hasConnectionIssues = false;

      // Load dashboard stats first (most important, 8 queries)
      let statsData: DashboardStats | null = null;
      try {
        statsData = await apiClient.getDashboardStats();
      } catch (err: any) {
        if (err.message?.includes('503') || err.message?.includes('Service Unavailable')) {
          hasConnectionIssues = true;
          console.warn(
            '[Dashboard] 503 error on stats, skipping remaining calls to prevent connection storm',
          );
        }
        throw err;
      }

      // Load payment data (1 query) - skip if connection issues
      let paymentsData: PaymentData[] = [];
      if (!hasConnectionIssues) {
        try {
          paymentsData = await apiClient.getPaymentData();
        } catch (err: any) {
          if (err.message?.includes('503')) {
            hasConnectionIssues = true;
            console.warn('[Dashboard] 503 error on payments, skipping remaining calls');
          } else {
            // Log but continue for non-503 errors
            console.error('[Dashboard] Payment data failed (non-503):', err);
          }
        }
      }

      // Load requests data (1 query) - skip if connection issues
      let requestsData: PreScreeningRequest[] = [];
      if (!hasConnectionIssues) {
        try {
          requestsData = await apiClient.getPreScreeningRequests();
        } catch (err: any) {
          if (err.message?.includes('503')) {
            hasConnectionIssues = true;
            console.warn('[Dashboard] 503 error on requests, skipping remaining calls');
          } else {
            console.error('[Dashboard] Request data failed (non-503):', err);
          }
        }
      }

      // Load activity data (3 queries) - skip if connection issues
      let activityData: RecentActivity[] = [];
      if (!hasConnectionIssues) {
        try {
          activityData = await apiClient.getRecentActivity();
        } catch (err: any) {
          if (err.message?.includes('503')) {
            hasConnectionIssues = true;
            console.warn('[Dashboard] 503 error on activity, skipping notifications');
          } else {
            console.error('[Dashboard] Activity data failed (non-503):', err);
          }
        }
      }

      // Load notifications (1 query) - skip if connection issues
      let notificationsData: DashboardNotification[] = [];
      if (!hasConnectionIssues) {
        try {
          notificationsData = await apiClient.getNotifications();
        } catch (err: any) {
          // Notifications are optional, don't break dashboard for this
          console.error('[Dashboard] Notifications failed:', err);
          notificationsData = [];
        }
      }

      // Decrypt encrypted fields in requests
      const decryptedRequests = await Promise.all(
        requestsData.map(async (request) => {
          try {
            // Decrypt each sensitive field that might be encrypted
            return {
              ...request,
              plaintiff: await decryptField(request.plaintiff, request.caseId ?? undefined),
              attorneyName: await decryptField(request.attorneyName, request.caseId ?? undefined),
              attorneyEmail: await decryptField(request.attorneyEmail, request.caseId ?? undefined),
              firmName: await decryptField(request.firmName, request.caseId ?? undefined),
              notes: await decryptField(request.notes, request.caseId ?? undefined),
              documentUrl: await decryptField(request.documentUrl, request.caseId ?? undefined),
            };
          } catch (error) {
            console.error('❌ Failed to decrypt request fields:', request.id, error);
            return request;
          }
        }),
      );

      // Decrypt encrypted fields in activity
      const decryptedActivity = await Promise.all(
        activityData.map(async (activity) => {
          try {
            // Decrypt activity fields that might be encrypted
            return {
              ...activity,
              action: await decryptField(activity.action, activity.caseId),
              case: await decryptField(activity.case, activity.caseId),
            };
          } catch (error) {
            console.error('❌ Failed to decrypt activity fields:', error);
            return activity;
          }
        }),
      );

      setDashboardStats(statsData);
      setPaymentData(paymentsData);
      setRecentRequests(decryptedRequests);
      setRecentActivity(decryptedActivity);
      setNotifications(notificationsData);

      // Warn user if dashboard is incomplete due to connection issues
      if (hasConnectionIssues && !silent) {
        toast.warning(
          'Dashboard loaded with limited data due to high server load. Please try refreshing in a moment.',
        );
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(errorMessage);
      if (!silent) {
        toast.error('Failed to load dashboard data');
      }
      console.error('Dashboard data loading error:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Keep ref updated with latest loadDashboardData function
  loadDashboardDataRef.current = loadDashboardData;

  const handleRefresh = () => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshRef.current;

    if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL_MS) {
      // Show message that refresh is throttled
      const secondsRemaining = Math.ceil((MIN_REFRESH_INTERVAL_MS - timeSinceLastRefresh) / 1000);
      toast.info(`Please wait ${secondsRemaining}s before refreshing again`);
      return;
    }

    lastRefreshRef.current = now;
    loadDashboardData(true);
    toast.success('Dashboard refreshed');
  };

  const handleOpenInviteModal = () => {
    trackInviteEvent('invite_client_modal_opened', { source: 'dashboard' });
    setIsInviteModalOpen(true);
    // Modal will fetch attorneys from server with decryption via fetchFromServer prop
  };

  const handleUnlockSuccess = async (passphrase: string) => {
    try {
      await unlockEncryption(passphrase);
      setShowUnlockModal(false);
      // Refresh dashboard data after unlocking to decrypt fields
      loadDashboardData(true);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to unlock encryption');
    }
  };

  const handleViewIntakeForm = async () => {
    try {
      // Fetch latest vanity URL from API to ensure we have the correct value
      const data = await apiClient.getVanityUrlSettings();
      let vanityUrl: string | undefined = data.vanityUrl;

      // Fallback: try user context if API returns empty
      if (!vanityUrl) {
        vanityUrl = user?.vanityUrl;
      }

      // Final fallback: create from email
      if (!vanityUrl && user?.email) {
        vanityUrl = user.email
          .split('@')[0]
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-');
      }

      if (vanityUrl) {
        // Open intake form in new tab
        const baseUrl = window.location.origin;
        const intakeUrl = `${baseUrl}/${vanityUrl}`;
        window.open(intakeUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('[Dashboard] Failed to fetch vanityUrl:', error);
      // Use fallback from user context
      const vanityUrl =
        user?.vanityUrl ||
        user?.email
          ?.split('@')[0]
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-');
      if (vanityUrl) {
        const baseUrl = window.location.origin;
        const intakeUrl = `${baseUrl}/${vanityUrl}`;
        window.open(intakeUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleMarkNotificationRead = async (id: string) => {
    try {
      await apiClient.markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await apiClient.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      toast.error('Failed to mark notifications as read');
    }
  };

  const unreadNotifications = notifications.filter((n) => !n.read);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-[#EAE0CB]/50 text-[#3A2718] border-[#3A2718]/15';
      case 'accepted':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'rejected':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6 bg-gray-50">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Dashboard</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => loadDashboardData()} className="bg-[#3A2718] hover:bg-[#241610]">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 bg-gray-50">
      {/* Subscription status — shown to doctors + doctor team members. Renders
          nothing for other roles. */}
      <DoctorSubscriptionCard
        state={doctorSubscriptionState}
        onPlanChanged={refreshDoctorSubscription}
      />

      {/* Hero Banner with Intake Form */}
      <HeroBanner
        userEmail={user?.email}
        vanityUrl={user?.vanityUrl}
        onPreviewIntakeForm={handleViewIntakeForm}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-gray-900 text-xl sm:text-2xl font-semibold">Dashboard</h1>
          <p className="text-gray-600 text-sm sm:text-base mt-1">
            Welcome back, {user?.name || 'Doctor'}
          </p>
          <LearnMoreLink article="doctorDashboard" className="mt-1" />
        </div>
        <div className="flex items-center gap-3">
          {/* Encryption Status Toggle */}
          {encryptionStatus?.setup && (
            <div data-tour="doctor-dashboard-encryption">
              {!isUnlocked ? (
                <Button
                  variant="outline"
                  className="bg-[#EAE0CB]/50 border-[#3A2718]/20 text-[#3A2718] hover:bg-[#EAE0CB]/80 shadow-sm"
                  onClick={() => setShowUnlockModal(true)}
                >
                  <Lock className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Unlock Encryption</span>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100 shadow-sm"
                  onClick={lockEncryption}
                >
                  <Lock className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Lock Encryption</span>
                </Button>
              )}
            </div>
          )}
          <Button
            onClick={handleOpenInviteModal}
            className="bg-[#3A2718] hover:bg-[#241610] text-white shadow-sm"
          >
            <Mail className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Invite Clients</span>
          </Button>
          <Button
            variant="outline"
            className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''} ${isRefreshing ? '' : 'sm:mr-2'}`}
            />
            <span className="hidden sm:inline">
              {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
            </span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
        data-tour="doctor-dashboard-stats"
      >
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-gray-600 text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-gray-900">
              ${dashboardStats?.totalRevenue?.amount?.toLocaleString() || '0'}
            </div>
            <div
              className={`flex items-center text-xs mt-1 ${
                dashboardStats?.totalRevenue?.trend === 'up'
                  ? 'text-green-600'
                  : dashboardStats?.totalRevenue?.trend === 'down'
                    ? 'text-red-600'
                    : 'text-gray-600'
              }`}
            >
              {dashboardStats?.totalRevenue?.trend === 'up' ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : dashboardStats?.totalRevenue?.trend === 'down' ? (
                <TrendingDown className="h-3 w-3 mr-1" />
              ) : (
                <Minus className="h-3 w-3 mr-1" />
              )}
              {dashboardStats?.totalRevenue?.description || 'No data'}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-gray-600 text-sm font-medium">Active Cases</CardTitle>
            <FileText className="h-4 w-4 text-[#A66B32]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-gray-900">
              {dashboardStats?.activeCases?.count || 0}
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {dashboardStats?.activeCases?.description || 'No pending cases'}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-gray-600 text-sm font-medium">Pending Invoices</CardTitle>
            <Clock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-gray-900">
              {dashboardStats?.pendingInvoices?.count || 0}
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {dashboardStats?.pendingInvoices?.description || 'No pending invoices'}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-gray-600 text-sm font-medium">Completion Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-gray-900">
              {dashboardStats?.completionRate?.percentage || 0}%
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {dashboardStats?.completionRate?.description || 'No data'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link Card */}
      {referralStats?.referralCode && (
        <Card
          className="bg-[#F5EDD8] border-[#3A2718]/15 shadow-sm"
          data-tour="doctor-dashboard-referral"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 bg-[#EAE0CB] rounded-lg flex items-center justify-center flex-shrink-0">
                <Gift className="h-5 w-5 text-[#A66B32]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">Refer &amp; Earn</p>
                <p className="text-xs text-gray-600">
                  Earn 25% of platform fees for 12 months per referral
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <code className="text-xs text-[#3A2718] font-mono bg-white/60 px-2 py-1 rounded hidden sm:block">
                  {`${window.location.origin}/?ref=${referralStats.referralCode}`}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#3A2718]/20 text-[#3A2718] hover:bg-[#EAE0CB]/70"
                  onClick={async () => {
                    const link = `${window.location.origin}/?ref=${referralStats.referralCode}`;
                    try {
                      await navigator.clipboard.writeText(link);
                      setCopiedReferral(true);
                      toast.success('Referral link copied!');
                      setTimeout(() => setCopiedReferral(false), 2000);
                    } catch {
                      toast.error('Failed to copy');
                    }
                  }}
                >
                  {copiedReferral ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  <span className="ml-1 hidden sm:inline">
                    {copiedReferral ? 'Copied' : 'Copy Link'}
                  </span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Activity & Notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-stretch">
        {/* Notifications */}
        {(() => {
          const pendingRequests = recentRequests.filter((r) => r.status === 'pending');
          const allNotificationItems: Array<{
            id: string;
            type: 'intake' | 'payment' | 'case' | 'deliverable';
            title: string;
            message: string;
            date: Date;
            requestId?: string;
            invoiceId?: string;
            caseId?: string;
            notificationId?: string;
          }> = [
            ...pendingRequests.map((r) => ({
              id: `req-${r.id}`,
              type: 'intake' as const,
              title: 'New Intake Request',
              message: `${r.plaintiff} — ${r.firmName}`,
              date: new Date(r.dateSubmitted),
              requestId: r.id,
            })),
            ...unreadNotifications.map((n) => {
              const data = n.data as Record<string, unknown> | undefined;
              const notifType = n.type;
              let itemType: 'intake' | 'payment' | 'case' | 'deliverable' = 'payment';
              if (['intake_request'].includes(notifType)) {
                itemType = 'intake';
              } else if (
                ['case_update', 'case_updated', 'file_uploaded', 'case_status_changed'].includes(
                  notifType,
                )
              ) {
                itemType = 'case';
              } else if (
                [
                  'deliverable_request',
                  'deliverable_cancelled',
                  'deliverable_status_changed',
                ].includes(notifType)
              ) {
                itemType = 'deliverable';
              }
              return {
                id: `notif-${n.id}`,
                type: itemType,
                title: n.title,
                message: n.message,
                date: new Date(n.createdAt),
                invoiceId: data?.invoiceId as string | undefined,
                caseId: data?.caseId as string | undefined,
                requestId: data?.intakeRequestId as string | undefined,
                notificationId: n.id,
              };
            }),
          ]
            .sort((a, b) => b.date.getTime() - a.date.getTime())
            .slice(0, 5);

          return (
            <Card
              className="bg-white border-gray-200 shadow-sm"
              data-tour="doctor-dashboard-notifications"
            >
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Recent Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {allNotificationItems.length > 0 ? (
                  allNotificationItems.map((item) => (
                    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance; keyboard path provided by adjacent controls
                    // biome-ignore lint/a11y/noStaticElementInteractions: pointer-only affordance; keyboard path provided by adjacent controls
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => {
                        if (item.type === 'intake' && item.requestId) {
                          onNavigateToRequest?.(item.requestId);
                        } else if (
                          (item.type === 'case' || item.type === 'deliverable') &&
                          item.caseId
                        ) {
                          onNavigateToCase?.(item.caseId);
                        } else if (item.type === 'payment' && item.invoiceId) {
                          onNavigateToInvoice?.(item.invoiceId);
                        }
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            item.type === 'intake'
                              ? 'bg-[#EAE0CB]'
                              : item.type === 'case' || item.type === 'deliverable'
                                ? 'bg-purple-100'
                                : 'bg-green-100'
                          }`}
                        >
                          {item.type === 'intake' ? (
                            <FileText className="h-4 w-4 text-[#A66B32]" />
                          ) : item.type === 'case' || item.type === 'deliverable' ? (
                            <FolderOpen className="h-4 w-4 text-purple-600" />
                          ) : (
                            <DollarSign className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{item.title}</p>
                          <p className="text-sm text-gray-600 truncate">{item.message}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {item.date.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-gray-900 font-medium mb-1">No notifications</h3>
                    <p className="text-gray-600 text-sm">
                      New requests and payment alerts will appear here
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Payments Timeline */}
        <Card className="bg-white border-gray-200 shadow-sm flex flex-col">
          <CardHeader>
            <CardTitle className="text-gray-900">Payment Activity</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0">
            {paymentData.length > 0 ? (
              <div className="w-full h-full min-h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={paymentData}>
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                      tickFormatter={(value: number) => `$${value.toLocaleString()}`}
                      width={60}
                    />
                    <Tooltip
                      formatter={(value: number) => [`$${value.toLocaleString()}`, 'Amount']}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                      labelStyle={{ color: '#374151', fontWeight: 500 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="#A66B32"
                      strokeWidth={3}
                      dot={{ fill: '#A66B32', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-8">
                <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-gray-900 font-medium mb-1">No payment data</h3>
                <p className="text-gray-600 text-sm">Payment activity will be displayed here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-gray-900">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: index keys are the established behavior for these order-stable lists
                key={index}
                className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="w-2 h-2 bg-[#A66B32] rounded-full"></div>
                <div className="flex-1">
                  <p className="text-gray-900 text-sm">{activity.action}</p>
                  <p className="text-gray-600 text-xs">
                    {activity.case} • {activity.time}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-gray-900 font-medium mb-1">No recent activity</h3>
              <p className="text-gray-600 text-sm">Your activity will be tracked here</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* First-time user onboarding modal */}
      <HowItWorksModal
        isOpen={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
        isFirstTimeUser={true}
        onNavigateToSettings={onNavigateToSettings}
        onSetupEncryption={onNavigateToSettings}
        showBillingPrompt={!billingSetupComplete}
        showEncryptionPrompt={!encryptionStatus?.setup}
      />

      {/* Unlock encryption modal */}
      {showUnlockModal && (
        <PassphraseEntry
          onSuccess={handleUnlockSuccess}
          onCancel={() => setShowUnlockModal(false)}
          allowCancel={true}
        />
      )}

      {/* Invite Clients Modal */}
      <InviteClientsModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        attorneys={[]}
        doctorName={
          user?.firstName && user?.lastName
            ? `${user.firstName} ${user.lastName}`
            : user?.name || 'Doctor'
        }
        specialty={user?.specialty}
        vanityUrl={user?.vanityUrl}
        fetchFromServer={true}
      />
    </div>
  );
}
